import { useRef, useCallback } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

declare global {
    interface Window {
        CCapture: any;
        _sceneOnRecordComplete?: (blob: Blob) => Promise<void>;
        _sceneRecordProgress?: number;
    }
}

interface UseVideoRecorderProps {
    onRecordingStart?: () => void;
    onRecordingComplete?: (blob: Blob) => void;
    autoDownload?: boolean; 
}

export const useVideoRecorder = ({ onRecordingStart, onRecordingComplete, autoDownload = false }: UseVideoRecorderProps = {}) => {
    const capturerRef = useRef<any>(null);
    const isRecordingRef = useRef(false);
    const framesRecordedRef = useRef(0);
    const initialRotationRef = useRef<number>(0);
    const modelGroupRef = useRef<THREE.Group | null>(null);
    
    const totalFrames = 360; // 6 seconds at 60fps - slower, smoother rotation

    const startRecording = useCallback((modelGroup: THREE.Group) => {
        if (!window.CCapture) {
             console.error('CCapture not found');
             alert('Video recording library not loaded. Please refresh the page.');
             return;
        }

        // console.log('[useVideoRecorder] Starting recording...');

        // Initialize Capturer
        capturerRef.current = new window.CCapture({
            format: 'webm',
            framerate: 60,
            verbose: false, // Reduce console spam
            quality: 100,
            name: 'mockup_video',
        });

        capturerRef.current.start();
        
        isRecordingRef.current = true;
        framesRecordedRef.current = 0;
        modelGroupRef.current = modelGroup;
        initialRotationRef.current = modelGroup.rotation.y;
        
        // Reset progress
        window._sceneRecordProgress = 0;
        
        onRecordingStart?.();
        // console.log('[useVideoRecorder] Recording started');
    }, [onRecordingStart]);

    const stopRecording = useCallback(() => {
        // console.log('[useVideoRecorder] Stopping recording...');
        isRecordingRef.current = false;
        
        if (!capturerRef.current) {
            console.warn('[useVideoRecorder] No capturer to stop.');
            return;
        }
        
        capturerRef.current.stop();
        capturerRef.current.save((blob: Blob) => {
            // console.log('[useVideoRecorder] Recording saved, blob size:', blob.size);
            onRecordingComplete?.(blob);
            
            if (autoDownload) {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `mockup-${Date.now()}.webm`;
                a.click();
                URL.revokeObjectURL(url);
            }
        });

        // Reset rotation
        if (modelGroupRef.current) {
            modelGroupRef.current.rotation.y = initialRotationRef.current;
        }
        modelGroupRef.current = null;
        window._sceneRecordProgress = 0;
    }, [onRecordingComplete, autoDownload]);

    // Frame loop - using refs to avoid stale closure
    useFrame(({ gl }) => {
        if (!isRecordingRef.current || !capturerRef.current || !modelGroupRef.current) return;

        const framesRecorded = framesRecordedRef.current;
        
        // Auto-spin logic: 360 degrees over totalFrames
        const angle = (framesRecorded / totalFrames) * Math.PI * 2;
        modelGroupRef.current.rotation.y = initialRotationRef.current + angle;

        // Capture frame
        try {
            capturerRef.current.capture(gl.domElement);
        } catch (e) {
            console.error('[useVideoRecorder] Capture failed:', e);
            isRecordingRef.current = false;
            return;
        }
        
        // Increment frame count
        framesRecordedRef.current++;
        const nextFrame = framesRecordedRef.current;
        
        // Report progress to overlay
        const progressPercent = Math.round((nextFrame / totalFrames) * 100);
        window._sceneRecordProgress = progressPercent;
        
        if (nextFrame % 60 === 0) {
            // console.log(`[useVideoRecorder] Progress: ${progressPercent}%`);
        }
        
        // Check if complete
        if (nextFrame >= totalFrames) {
            stopRecording();
        }
    });

    return {
        isRecording: isRecordingRef.current,
        progress: Math.round((framesRecordedRef.current / totalFrames) * 100),
        startRecording
    };
};
